import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { Tenant } from '../../../model/tenant';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { TenantsService } from '../../../services/tenants.service';
import { MessageService } from 'primeng/api';
import { EncodersService } from '../../../services/encoders.service';
import { Encoder } from '../../../model/encoder';
import { EncoderPersistRequest } from '../../../model/requests/encoder-persist-request';
import { noSpecialCharacters } from '../../../shared/custom-validators';
import { ComponentCanDeactivate } from '../../../common/can-deactivate/component-can-deactivate';
import { AuthGuard } from '../../../common/auth-guard.service';
import { CommonMessagesService } from "../../../services/common-messages.service";
import { JsonEditorService } from '../../../services/json-editor.service';
import { AssetLoaderService } from '../../../services/asset-loader.service';
import { JsonEditorComponent } from 'ang-jsoneditor';

@Component({
  selector: 'app-encoders-form',
  templateUrl: './encoders-form.component.html',
  styleUrls: ['./encoders-form.component.scss']
})
export class EncodersFormComponent implements OnInit, ComponentCanDeactivate {

  private readonly commandsSchemaAssetPath = 'form-config/encoders/commands-schema.json';

  public encoder: Encoder;

  public tenants: Tenant[];

  public encoderForm: FormGroup;
  public persistingChanges: any;
  public sourceVisible: boolean;
  public formSuccess: boolean;

  public mode: 'create' | 'edit';
  public editInfo: { tenantUuid: string, oldName: string };

  @ViewChild('commandsSchemaEditor', {static: false})
  commandsSchemaEditor: JsonEditorComponent;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private i18n: TranslocoService,
    private authGuard: AuthGuard,
    private encodersService: EncodersService,
    private tenantsService: TenantsService,
    private messageService: MessageService,
    private commonMessagesService: CommonMessagesService,
    private assetLoaderService: AssetLoaderService,
    public jsonEditorService: JsonEditorService
  ) {
  }

  ngOnInit(): void {
    this.route.url.subscribe(urlSegment => {
      if (urlSegment[0].path === 'new') {
        this.mode = 'create';
        this.tenantsService.getTenantSummaries().toPromise().then(response => {
          this.tenants = response.tenants;
          this.loadCreationForm();
        });
      } else {
        this.mode = 'edit';
        this.loadEditionForm(urlSegment[0].path, urlSegment[1].path);
      }
    });
  }

  loadCreationForm(): void {
    this.encoder = new Encoder();
    if (!this.authGuard.superAdminRequired()) {
      this.encoder.tenant = this.tenants[0];
    }
    this.createForm(this.encoder);
  }

  loadEditionForm(tenantUuid, name): void {
    this.tenantsService.getTenantEncoder(tenantUuid, name).toPromise().then(response => {
      this.encoder = response.encoder;
      this.tenants = [this.encoder.tenant];
      this.editInfo = {tenantUuid: this.encoder.tenant.uuid, oldName: this.encoder.name};
      this.encoder.commandsSchema = JSON.parse(this.encoder.commandsSchema);
      this.createForm(this.encoder);
    });
  }

  private createForm(encoder: Encoder): void {
    this.encoderForm = this.formBuilder.group({
      name: [encoder.name, [Validators.required, Validators.maxLength(255), noSpecialCharacters]],
      commandsSchema: [encoder.commandsSchema, [Validators.required, Validators.maxLength(255)]],
      tenant: [encoder.tenant, [Validators.required]]
    });
    this.encoderForm.markAsPending();

    this.assetLoaderService.loadAssetWithRoute(this.commandsSchemaAssetPath).toPromise().then(config => {
      this.commandsSchemaEditor.setSchema(config.schema, {});
      if (this.mode === 'create') {
        this.encoderForm.get('commandsSchema').setValue(config.template);
      }
    });

    if (!this.authGuard.superAdminRequired()) {
      this.encoderForm.get('tenant').disable();
    }

    if (this.mode === 'edit') {
      this.encoderForm.get('tenant').disable();
    }
  }

  isCommandsSchemaValid(): boolean {
    if (this.commandsSchemaEditor.isValidJson()) {
      return this.commandsSchemaEditor.getEditor().validateSchema.errors === null;
    }
    return false;
  }

  persistEncoderData(): void {
    if (!this.encoderForm.valid) {
      return;
    }

    if (!this.isCommandsSchemaValid()) {
      const summary = this.i18n.translate('error.summary.configErrors');
      const detail = this.i18n.translate('error.jsonEditor.configErrors');
      this.messageService.add({severity: 'error', summary, detail, sticky: true});
      return;
    }

    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.encodersService.createEncoder(persistData).toPromise();
    } else {
      promise = this.encodersService.editEncoder(persistData).toPromise();
    }

    promise.then(response => {
      this.formSuccess = true;
      this.router.navigate([`/system/encoders`]);
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): EncoderPersistRequest {
    const formValue = this.encoderForm.getRawValue();
    return {
      name: formValue.name,
      commandsSchema: JSON.stringify(formValue.commandsSchema),
      tenantUuid: this.mode === 'edit' ? this.editInfo.tenantUuid : formValue.tenant.uuid,
      oldName: this.mode === 'edit' ? this.editInfo.oldName : null
    };
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || this.encoderForm.untouched;
  }

}
